import React, { useEffect } from "react";
import type { AppProps } from "next/app";
import Script from "next/script";
import { ChakraProvider } from "@chakra-ui/react";
import { DefaultSeo } from "next-seo";
import theme from "../theme";
import { analytics } from "../utils/analytics";
import { useRouter } from "next/router";
import "../styles/globals.css";

import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();

  // Track page views
  useEffect(() => {
    const handleRouteChange = (url: string) => {
      analytics.trackPageView(url);
    };

    // Track initial page view
    analytics.trackPageView(router.pathname);

    // Track subsequent page views
    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router]);

  useEffect(() => {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com',
      person_profiles: 'identified_only',
      defaults: '2025-05-24',
      // Enable debug mode in development
      loaded: (posthog) => {
        if (process.env.NODE_ENV === 'development') posthog.debug()
      }
    })
  }, [])

  return (
    <>
      {/* Google Tag Manager */}
      <Script id="google-tag-manager" strategy="afterInteractive">
        {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-MXTPHWGQ');
        `}
      </Script>
      {/* Google Analytics */}
      <Script
        src="https://www.googletagmanager.com/gtag/js?id=G-MYFHVWJEN7"
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-MYFHVWJEN7');
        `}
      </Script>
      <DefaultSeo
        defaultTitle="Loma AI"
        description="Use our AI and your social media to instantly launch a new customized commerce platform."
        openGraph={{
          type: "website",
          locale: "en_US",
          url: "https://www.loma.ai/",
          siteName: "Loma AI",
          images: [
            {
              url: "https://loma-files.s3.us-west-1.amazonaws.com/loma-logos/social-media-kit/Cover2.jpg",
              width: 1200,
              height: 630,
              alt: "Loma AI",
            },
          ],
        }}
        twitter={{
          site: "https://www.loma.ai",
          cardType: "summary_large_image",
        }}
      />
      <PostHogProvider client={posthog}>
        <ChakraProvider theme={theme}>
          <Component {...pageProps} />
        </ChakraProvider>
      </PostHogProvider>
    </>
  );
}
